import {
    DistributionType,
    DrawerType,
    EstateFlowchartState,
    FlowchartEstateItemType
} from "../model/EstateFlowchartTypes";
import {EstateItemType} from "../forms/EstateItemType";
import {findIrrevocableTrustById, findMiscellaneousById, findTrustInFlowchart} from "./DefUtils";
import {SideDrawerDetails} from "../components/SideDrawer/SideDrawer";
import {PowerOfAppointment} from "../components/PowersOfAppointmentSection/PowerOfAppointment";

export type SideDrawerField = {
    property: string;
    label: string;
    type?: string;
    height: number;
    maxHeight?: number;
    section?: string;
    value?: string;
}

export type CardDetailFieldsSections = {
    section1: SideDrawerField[],
    section2: SideDrawerField[],
    powersOfAppointment: SideDrawerField[]
};

const commonSectionTwoFields: SideDrawerField[] = [
    {property: "grantors", label: "Grantors", type: "list", height: 20},
    {property: "trustees", label: "Trustees", type: "list", height: 20},
    {property: "successors", label: "Successors", type: "orderedList", height: 20}
];

const createEstateItemInfo = (estateItemType: EstateItemType) => {
    const legalNameLabel = estateItemType === EstateItemType.WILL ? "Legal Name of Will" : "Legal Name of Trust or Entity";
    const commonBasicInfo: SideDrawerField[] = [
        {property: "basicInformation", label: "Basic Information", type: "sectionHeading", height: 40},
        {property: "legalName", label: legalNameLabel, height: 40},
        {property: "nickName", label: "Nickname", height: 40},
        {property: "trustCreationDate", label: "Date Created", type: "date", height: 40},
        {property: "recordOfAmendment", label: "Record of Amendments", height: 56, maxHeight: 100},
    ];
    return commonBasicInfo
}

const createTrustDistributionInfo = (distributionType: DistributionType) => {
    const beneficiaryLabel = distributionType === DistributionType.MARITAL ? "Beneficiary" : "Beneficiaries";
    const commonTrustInfo: SideDrawerField[] = [
        {property: "trustDetails", label: "Trust Details", type: "sectionHeading", height: 40},
        {property: "name", label: "Trust Name", height: 40},
        {property: "tags", label: "GST Tags", height: 40},
        {property: "trustees", label: "Trustees", type: "list", height: 20},
        {property: "successors", label: "Successors", type: "orderedList", height: 20},
        {property: "beneficiaries", label: beneficiaryLabel, type: "list", height: 20},
        {property: "funding", label: "Funding", type: "textarea", height: 56, maxHeight: 100},
        {property: "distributions", label: "Distributions", type: "textarea", height: 56, maxHeight: 100},
        {property: "termination", label: "Termination", type: "textarea", height: 56, maxHeight: 100},
    ]
    return commonTrustInfo;
}

const powersOfAppointmentFields = (): SideDrawerField[] => {
    return [
        {property: "title", label: "Title", height: 40},
        {property: "type", label: "Type", height: 40},
        {property: "transferType", label: "Transfer Type", height: 40},
        {property: "powerHolder", label: "Power Holder", height: 40},
        {
            property: "permissibleAppointees",
            label: "Permissible Appointees",
            type: "textarea",
            height: 56,
            maxHeight: 100
        },
        {property: "additionalDetails", label: "Additional Details", type: "textarea", height: 56, maxHeight: 100},
    ];
};

const NonTrustDistributionInfo: SideDrawerField[] = [
    {property: "funding", label: "Funding", type: "textarea", height: 56, maxHeight: 100},
    {property: "additionalDetails", label: "Additional Details", type: "textarea", height: 56, maxHeight: 100},
];

export const drawerFieldConfig = {
    REVOCABLE: {
        section1: createEstateItemInfo(EstateItemType.REVOCABLE_TRUST),
        section2: [
            {property: "trustDetails", label: "Trust Details", type: "sectionHeading", height: 40},
            ...commonSectionTwoFields,
            {property: "funding", label: "Funding", type: "textarea", height: 56, maxHeight: 100},
            {property: "investmentAdvisor", label: "Investment Advisor", height: 40},
            {property: "trustProtector", label: "Trust Protector", height: 40},
            {property: "trustJurisdiction", label: "Trust Jurisdiction", type: "state", height: 40},
        ],
    },
    IRREVOCABLE: {
        section1: createEstateItemInfo(EstateItemType.IRREVOCABLE_TRUST),
        section2: [
            {property: "trustDetails", label: "Trust Details", type: "sectionHeading", height: 40},
            {property: "tags", label: "GST Tags", height: 40},
            ...commonSectionTwoFields,
            {property: "beneficiaries", label: "Beneficiaries", type: "list", height: 20},
            {property: "funding", label: "Funding", type: "textarea", height: 56, maxHeight: 100},
            {property: "distributions", label: "Distributions", type: "textarea", height: 56, maxHeight: 100},
            {
                property: "terminatesUponSpecificDeath",
                label: "Termination",
                type: "termination",
                height: 40,
                maxHeight: 100
            },
            {property: "investmentAdvisor", label: "Investment Advisor", height: 40},
            {property: "trustProtector", label: "Trust Protector", height: 40},
            {property: "trustJurisdiction", label: "Trust Jurisdiction", type: "state", height: 40},
        ],
    },
    WILL: {
        section1: createEstateItemInfo(EstateItemType.WILL),
        section2: [
            {property: "trustDetails", label: "Will Details", type: "sectionHeading", height: 40},
            {property: "creationDate", label: "Creation Date", type: "date", height: 40},
            {property: "executors", label: "Executor", type: "orderedList", height: 20},
            {property: "guardiansForMinors", label: "Guardians for Minors", type: "orderedList", height: 20},
            {property: "funding", label: "Funding", type: "textarea", height: 56, maxHeight: 100},
            {
                property: "exercisesOfPoa",
                label: "Exercises of Powers of Appointment",
                type: "textarea",
                height: 56,
                maxHeight: 100
            },
            {
                property: "personalProperty",
                label: "Tangible Personal Property",
                type: "textarea",
                height: 56,
                maxHeight: 100
            },
            {property: "bequests", label: "Specific Bequests", type: "textarea", height: 56, maxHeight: 100},
            {property: "jurisdiction", label: "Jurisdiction", type: "state", height: 40},
        ]
    },
    CHARITY: {
        section1: [{
            property: "charityBeneficiaries",
            label: "Beneficiaries",
            type: "unorderedList",
            height: 20
        }, ...NonTrustDistributionInfo],
        section2: []
    },
    CHARITY_WITH_ONLY_NAME: {
        section1: [{property: "name", label: "Beneficiary", height: 40}, ...NonTrustDistributionInfo],
        section2: []
    },
    OUTRIGHT: {
        section1: [{
            property: "outrightMembers",
            label: "Name of Recipient",
            type: "unorderedList",
            height: 20
        }, ...NonTrustDistributionInfo],
        section2: []
    },
    MARITAL: {
        section1: createTrustDistributionInfo(DistributionType.MARITAL),
        section2: []
    },
    CREDIT_SHELTER: {
        section1: createTrustDistributionInfo(DistributionType.CREDIT_SHELTER),
        section2: []
    },
    IN_TRUST: {
        section1: createTrustDistributionInfo(DistributionType.IN_TRUST),
        section2: []
    },
    SURVIVOR: {
        section1: createTrustDistributionInfo(DistributionType.SURVIVOR),
        section2: []
    },
    MISCELLANEOUS: {
        section1: [
            {property: "basicDetails", label: "Basic Details", type: "sectionHeading", height: 40},
            {property: "name", label: "Nickname", height: 40},
        ],
        section2: [
            {property: "additionalDetailsHeading", label: "Additional Details", type: "sectionHeading", height: 40},
            {property: "beneficiaries", label: "Beneficiaries", type: "list", height: 20},
            {property: "funding", label: "Funding", type: "textarea", height: 56, maxHeight: 100},
            {property: "additionalDetails", label: "Additional Details", type: "textarea", height: 56, maxHeight: 100},
        ],
    },
};

export const createFilteredDrawerFieldConfig = (
    cardDetails: any,
    cardType: DrawerType,
    powersOfAppointment?: PowerOfAppointment[]
): CardDetailFieldsSections[] => {
    const config = getDrawerFieldConfig(cardType);
    const allSections = mapAllSections(config, cardDetails, powersOfAppointment);
    const groupedFields = groupFieldsByHeight(allSections);

    return groupedFields.map((fields) => ({
        section1: fields.filter((field) => field.section === "section1"),
        section2: fields.filter((field) => field.section === "section2"),
        powersOfAppointment: fields.filter((field) => field.section?.startsWith("powersOfAppointment_")),
    }));
};

const getDrawerFieldConfig = (cardType: DrawerType) => {
    if (cardType === DistributionType.CHARITY) {
        return drawerFieldConfig["CHARITY_WITH_ONLY_NAME"];
    }
    if (isDrawerFieldConfigKey(cardType)) {
        return drawerFieldConfig[cardType];
    }
    return undefined;
};

const mapAllSections = (
    config: any,
    cardDetails: any,
    powersOfAppointment?: PowerOfAppointment[]
) => {
    return {
        section1: mapFields(config.section1, cardDetails, "section1"),
        section2: mapFields(config.section2, cardDetails, "section2"),
        powersOfAppointment: mapPowersOfAppointmentFields(powersOfAppointment),
    };
};

const mapFields = (fields: any[], cardDetails: any, section: string) => {
    return fields?.map((field: any) => ({
        ...field,
        section,
        value: cardDetails[field.property] ?? null,
    })) || [];
};

const mapPowersOfAppointmentFields = (powersOfAppointment?: PowerOfAppointment[]) => {
    if (!powersOfAppointment || powersOfAppointment.length === 0) return [];

    return powersOfAppointment.flatMap((poa, index) => [
        {
            property: "title",
            section: `powersOfAppointment_${index + 1}`,
            value: `POWER OF APPOINTMENT ${index + 1}`,
            height: 40,
        },
        ...powersOfAppointmentFields().map((field: any) => ({
            ...field,
            section: `powersOfAppointment_${index + 1}`,
            value: poa[field.property as keyof PowerOfAppointment] ?? null,
        })),
    ]);
};

const groupFieldsByHeight = (allSections: any) => {
    const allFields = [...allSections.section1, ...allSections.section2, ...allSections.powersOfAppointment];
    const maxHeightPerPage = 650;

    let result: SideDrawerField[][] = [];
    let currentGroup: SideDrawerField[] = [];
    let fieldGroupHeight = 0;

    for (const field of allFields) {
        const fieldHeight = calculateFieldHeight(field);

        if (field.value !== null || field.type === "sectionHeading") {
            if (fieldGroupHeight + fieldHeight + 24 > maxHeightPerPage) {
                result.push(currentGroup);
                currentGroup = createContinuedSection(currentGroup);
                fieldGroupHeight = 0;
            }

            currentGroup.push(field);
            fieldGroupHeight += fieldHeight;
        }
    }

    if (currentGroup.length > 0) {
        result.push(currentGroup);
    }

    return result;
};

const calculateFieldHeight = (field: SideDrawerField) => {
    switch (field.type) {
        case "textarea":
        case "termination":
            return ((field.value?.length ?? 0) > 200 ? field.maxHeight ?? field.height : field.height) + 16;
        case "list":
        case "orderedList":
        case "unorderedList":
            return ((field.value?.length ?? 0) * field.height) + 16;
        default:
            return field?.height + 16;
    }
};

const createContinuedSection = (currentGroup: SideDrawerField[]) => {
    const lastSectionHeadingField = currentGroup.filter((field) => field.type === "sectionHeading").pop();

    if (lastSectionHeadingField && !lastSectionHeadingField.label.includes("(Continued)")) {
        return [
            {
                ...lastSectionHeadingField,
                label: `${lastSectionHeadingField.label} (Continued)`,
            } as SideDrawerField,
        ];
    }

    return lastSectionHeadingField ? [lastSectionHeadingField] : [];
};

export const getCardDetails = (selectedCardDetails: SideDrawerDetails, matchingFlowchart: EstateFlowchartState) => {
    let cardDetails;

    if (matchingFlowchart && selectedCardDetails) {
        switch (selectedCardDetails?.cardType) {
            case FlowchartEstateItemType.REVOCABLE:
                cardDetails = matchingFlowchart.revocableTrust;
                break;
            case FlowchartEstateItemType.IRREVOCABLE:
                cardDetails = findIrrevocableTrustById(selectedCardDetails?.id, matchingFlowchart);
                break;
            case DistributionType.CHARITY:
            case DistributionType.OUTRIGHT:
                cardDetails = findTrustInFlowchart(selectedCardDetails?.id,
                    selectedCardDetails.parentEstateItemId,
                    selectedCardDetails.parentEstateItemType,
                    matchingFlowchart);
                break;
            case DistributionType.WILL:
                if (matchingFlowchart.willSummary) {
                    if (selectedCardDetails.parentEstateItemId === matchingFlowchart.willSummary.primaryWill?.trustId) {
                        cardDetails = matchingFlowchart.willSummary.primaryWill;
                    } else if (selectedCardDetails.parentEstateItemId === matchingFlowchart.willSummary.partnerWill?.trustId) {
                        cardDetails = matchingFlowchart.willSummary.partnerWill;
                    } else {
                        cardDetails = undefined;
                    }
                }
                break;
            case DistributionType.MISCELLANEOUS:
                cardDetails = findMiscellaneousById(selectedCardDetails?.id, matchingFlowchart);
                break;
            case DistributionType.CREDIT_SHELTER:
            case DistributionType.IN_TRUST:
            case DistributionType.MARITAL:
            case DistributionType.SURVIVOR:
                cardDetails = findTrustInFlowchart(selectedCardDetails?.id,
                    selectedCardDetails?.parentEstateItemId,
                    selectedCardDetails?.parentEstateItemType,
                    matchingFlowchart);
        }
    }
    return cardDetails;
};

export function isDrawerFieldConfigKey(
    key: unknown
): key is keyof typeof drawerFieldConfig {
    return typeof key === 'string' && Object.hasOwn(drawerFieldConfig, key);
}
